import { useCallback, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import { useResource } from '../hooks/useResource.js';
import { Icon, SiteThumb, Spark } from './Brand.jsx';
import VeraRail from './VeraRail.jsx';
import { usePins } from '../pins/PinsContext.jsx';
import { preferredSlot } from '../lib/slots.js';

const STUDIO_NAV = [
  { to: '/', label: 'Workspace', end: true, icon: 'globe' },
  { to: '/sites', label: 'All sites', icon: 'server' },
  { to: '/config', label: 'Domains & routing', icon: 'globe' },
  { to: '/audit', label: 'Activity', icon: 'activity' },
  { to: '/users', label: 'Users', icon: 'users', adminOnly: true },
  { to: '/account', label: 'Account', icon: 'user' },
];

export default function AppShell() {
  const { user, signOut } = useAuth();
  const pins = usePins();
  const location = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [veraOpen, setVeraOpen] = useState(() => {
    try {
      return window.localStorage.getItem('presto-vera-rail') !== 'dismissed';
    } catch {
      return true;
    }
  });

  const loadSites = useCallback((signal) => endpoints.sites(signal), []);
  const loadHealth = useCallback((signal) => endpoints.health(signal), []);
  const sitesState = useResource(loadSites, []);
  const healthState = useResource(loadHealth, [], { intervalMs: 30000 });
  const sites = Array.isArray(sitesState.data) ? sitesState.data : [];
  const health = healthState.data;
  const currentName = location.pathname.match(/^\/sites\/([^/]+)/)?.[1];
  const currentSite = sites.find((site) => site.name === decodeURIComponent(currentName || ''));
  const pinnedSites = pins.sites
    .map((name) => sites.find((site) => site.name === name))
    .filter(Boolean);
  const allHealthy = health && health.siteCount > 0 && health.siteCount === health.runningCount;

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  }

  function setVeraVisibility(open) {
    setVeraOpen(open);
    try {
      window.localStorage.setItem('presto-vera-rail', open ? 'visible' : 'dismissed');
    } catch {
      // The rail still works for this session when browser storage is unavailable.
    }
  }

  return (
    <div className={`presto-app shell${navOpen ? ' shell--nav-open' : ''}${veraOpen ? '' : ' shell--vera-dismissed'}`}>
      <a className="skip-link" href="#main">Skip to content</a>

      <aside className="sidebar">
        <div className="sidebar__topline">
          <Link className="presto-logo" to="/" aria-label="Presto Studio workspace">
            <img className="presto-logo__light" src="/presto-logo.svg" alt="Presto Studio" />
            <img className="presto-logo__dark" src="/presto-logo-mono.svg" alt="Presto Studio" />
          </Link>
          <button
            type="button"
            className="btn btn--quiet sidebar__toggle"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            {navOpen ? 'Close' : 'Menu'}
          </button>
        </div>

        <nav className="sidebar__nav" aria-label="Workspace navigation">
          <section className="project-nav" aria-labelledby="project-nav-title">
            <div className="navlabel">
              <span className="cap" id="project-nav-title">Pinned projects</span>
              <Link to="/sites">View all</Link>
            </div>
            <div className="project-list">
              {pins.status === 'loading' ? (
                <p className="project-list__empty">Loading pinned sites…</p>
              ) : pinnedSites.length ? pinnedSites.map((site, index) => (
                <Link
                  className={`project-link${currentSite?.name === site.name ? ' is-active' : ''}`}
                  to={`/sites/${encodeURIComponent(site.name)}`}
                  key={site.name}
                  onClick={() => setNavOpen(false)}
                >
                  <SiteThumb site={site} index={index} />
                  <span className="project-link__copy">
                    <strong>{site.name}</strong>
                    <small>
                      <i className={`project-status project-status--${preferredSlot(site).unit === 'active' ? 'live' : 'draft'}`} />
                      {site.kind || 'site'} · {preferredSlot(site).unit === 'active' ? 'Running' : preferredSlot(site).unit || 'Unknown'}
                    </small>
                  </span>
                </Link>
              )) : (
                <p className="project-list__empty">
                  No pinned sites yet. <Link to="/sites">Choose sites to pin</Link>.
                </p>
              )}
            </div>
          </section>

          <section aria-labelledby="studio-nav-title">
            <div className="navlabel"><span className="cap" id="studio-nav-title">Studio</span></div>
            <ul className="studio-nav">
              {STUDIO_NAV.filter((item) => !item.adminOnly || user?.role === 'admin').map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) => `studio-link${isActive ? ' is-active' : ''}`}
                    onClick={() => setNavOpen(false)}
                  >
                    <Icon name={item.icon} />
                    <span>{item.label}</span>
                    {item.to === '/' && allHealthy ? <i className="studio-link__badge" aria-label="All systems healthy" /> : null}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        </nav>

        <div className="sidebar__foot">
          <div className="studio-health">
            <span className={`studio-health__dot${allHealthy ? ' is-live' : ''}`} />
            <span><strong>{allHealthy ? 'Studio online' : 'Checking studio'}</strong><small>{health ? `${health.runningCount} of ${health.siteCount} sites live` : 'Reading platform health'}</small></span>
          </div>
          <div className="whoami">
            <span className="whoami__avatar" aria-hidden="true">{(user?.username || '?').slice(0, 1).toUpperCase()}</span>
            <span className="whoami__copy">
              <span className="whoami__name">{user?.username || 'Unknown operator'}</span>
              <span className="whoami__role">{user?.role === 'admin' ? 'Admin' : 'User'}</span>
            </span>
            <button type="button" className="whoami__signout" onClick={handleSignOut} disabled={signingOut}>
              {signingOut ? '…' : 'Sign out'}
            </button>
          </div>
        </div>
      </aside>

      <main className="main" id="main"><Outlet /></main>

      {veraOpen ? (
        <VeraRail onDismiss={() => setVeraVisibility(false)} />
      ) : (
        <button type="button" className="vera-launcher" onClick={() => setVeraVisibility(true)}>
          <span className="vera-launcher__spark"><Spark size={16} /></span>
          <span><strong>Vera</strong><small>Coming soon</small></span>
        </button>
      )}

      {pins.error ? (
        <div className="pin-notice" role="alert">
          <span>{pins.error.message || 'Could not update pinned sites.'}</span>
          <button type="button" onClick={pins.clearError} aria-label="Dismiss pin error">×</button>
        </div>
      ) : null}
    </div>
  );
}
